import React, { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { UserBadge, colorFromId } from '../DocumentPreview';
import { MessageSquare, Plus, ArrowDownUp, FileText } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const safeDate = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso);
    } catch {
        return null;
    }
};

const formatRelative = (iso) => {
    const d = safeDate(iso);
    if (!d) return '';
    return formatDistanceToNow(d, { addSuffix: true, locale: fr });
};

const formatFull = (iso) => {
    const d = safeDate(iso);
    if (!d) return '';
    return format(d, "EEEE d MMMM yyyy 'à' HH:mm", { locale: fr });
};

const NOTE_TYPE_LABEL = {
    note: 'Note interne',
    appel: 'Appel',
    email: 'Email',
    sms: 'SMS',
    rdv: 'Rendez-vous',
};

/**
 * Dedicated notes feed for a dossier.
 * Includes ALL user-authored interactions (note / appel / email / sms / rdv),
 * excluding automatic status-change events (they live in the Historique tab).
 * Supports sort (newest/oldest) and free-text filtering by author or content.
 */
export const NotesTab = ({ dossier, onAddNote }) => {
    const [sort, setSort] = useState('desc'); // 'desc' = newest first
    const [filter, setFilter] = useState('');

    const notes = useMemo(() => {
        const all = (dossier?.interactions || []).filter(
            (it) => it.type_interaction && it.type_interaction !== 'statut'
        );
        const filtered = filter.trim()
            ? all.filter((n) => {
                const q = filter.trim().toLowerCase();
                return (
                    (n.contenu || '').toLowerCase().includes(q) ||
                    (n.created_by_name || '').toLowerCase().includes(q) ||
                    (NOTE_TYPE_LABEL[n.type_interaction] || '').toLowerCase().includes(q)
                );
            })
            : all;
        return [...filtered].sort((a, b) => {
            const dA = safeDate(a.created_at)?.getTime() || 0;
            const dB = safeDate(b.created_at)?.getTime() || 0;
            return sort === 'desc' ? dB - dA : dA - dB;
        });
    }, [dossier, sort, filter]);

    return (
        <div className="bg-white border border-[#E4E4E7] rounded-sm" data-testid="notes-tab">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 sm:p-6 border-b border-[#E4E4E7]">
                <div>
                    <h3 className="font-semibold text-[#0A0A0A]">Notes & interactions</h3>
                    <p className="text-xs text-[#52525B] mt-0.5">{notes.length} entrée{notes.length > 1 ? 's' : ''}</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <input
                        type="text"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="Rechercher dans les notes..."
                        className="h-9 px-3 rounded-sm border border-[#E4E4E7] text-sm w-full sm:w-64"
                        data-testid="notes-filter-input"
                    />
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSort((s) => (s === 'desc' ? 'asc' : 'desc'))}
                        className="rounded-sm"
                        data-testid="notes-sort-toggle"
                        title={sort === 'desc' ? 'Plus anciens d\'abord' : 'Plus récents d\'abord'}
                    >
                        <ArrowDownUp className="w-4 h-4 mr-1" />
                        {sort === 'desc' ? 'Plus récents' : 'Plus anciens'}
                    </Button>
                    <Button
                        onClick={onAddNote}
                        size="sm"
                        className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white rounded-sm"
                        data-testid="add-note-from-notes-tab-btn"
                    >
                        <Plus className="w-4 h-4 mr-1" /> Ajouter une note
                    </Button>
                </div>
            </div>
            <div className="p-4 sm:p-6">
                {notes.length === 0 ? (
                    <div className="text-center py-12 text-[#52525B]">
                        <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
                        <p className="text-sm">
                            {filter ? 'Aucune note ne correspond à votre recherche.' : 'Aucune note pour ce dossier.'}
                        </p>
                        {!filter && (
                            <p className="text-xs mt-1">Cliquez sur «&nbsp;Ajouter une note&nbsp;» pour démarrer le suivi.</p>
                        )}
                    </div>
                ) : (
                    <ul className="space-y-3">
                        {notes.map((n) => (
                            <li
                                key={n.id}
                                className="bg-[#FAFAFA] border-l-4 rounded-sm p-4"
                                style={{ borderLeftColor: colorFromId(n.created_by) }}
                                data-testid={`note-item-${n.id}`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="w-9 h-9 rounded-full bg-[#E85A24]/10 text-[#E85A24] flex items-center justify-center shrink-0">
                                        <MessageSquare className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap mb-2">
                                            <UserBadge userId={n.created_by} name={n.created_by_name || 'Système'} size="sm" />
                                            <span className="text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-[#E85A24]/10 text-[#E85A24]">
                                                {NOTE_TYPE_LABEL[n.type_interaction] || n.type_interaction}
                                            </span>
                                            <span className="text-xs text-[#A1A1AA]">·</span>
                                            <span
                                                className="text-xs text-[#52525B]"
                                                title={formatFull(n.created_at)}
                                            >
                                                {formatRelative(n.created_at)}
                                            </span>
                                        </div>
                                        <p className="text-sm text-[#0A0A0A] whitespace-pre-wrap break-words leading-relaxed">
                                            {n.contenu}
                                        </p>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};
